/** @typedef {import('../../api/walletConversions').WalletConversion} WalletConversion */

import { useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import { fetchWalletConversions } from '../../api';
import { handleQueryError } from '../../utils';

const PAGE_SIZE = 50;

// Poll loaded pages while any row is still INITIATED so transient
// PROCESSING rows transition to SUCCESS/FAILED without a manual refresh.
const INITIATED_REFETCH_MS = 15000;

const matchesSearch = (row, searchQuery) => {
  const needle = String(searchQuery || '')
    .trim()
    .toLowerCase();
  if (!needle) return true;

  return [
    row?.conversion_id,
    row?.from_currency,
    row?.to_currency,
    row?.from_display,
    row?.to_display,
    row?.reference
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
};

const useFetchWalletConversions = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isRefetching,
    refetch,
    isError,
    error
  } = useInfiniteQuery(
    ['walletConversions'],
    ({ pageParam = 0 }) =>
      fetchWalletConversions({
        limit: PAGE_SIZE,
        offset: pageParam
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage?.meta?.hasNext) return undefined;
        return allPages.reduce((sum, page) => sum + (page?.data?.length ?? 0), 0);
      },
      refetchInterval: (latestData) => {
        const pages = latestData?.pages ?? [];
        const hasInitiated = pages.some((page) =>
          (page?.data ?? []).some((row) => row?.status === 'INITIATED')
        );
        return hasInitiated ? INITIATED_REFETCH_MS : false;
      }
    }
  );

  if (isError) {
    handleQueryError(error, 'wallet conversions');
  }

  const conversions = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page?.data ?? []);
  }, [data]);

  const filteredConversions = useMemo(() => {
    let rows = conversions;
    if (searchQuery) {
      rows = rows.filter((row) => matchesSearch(row, searchQuery));
    }
    if (statusFilter) {
      rows = rows.filter((row) => row.status === statusFilter);
    }
    if (dateFilter.from || dateFilter.to) {
      const fromTime = dateFilter.from ? new Date(dateFilter.from).getTime() : -Infinity;
      // Inclusive upper bound — `to` interpreted as end-of-day.
      const toTime = dateFilter.to
        ? new Date(dateFilter.to).getTime() + 24 * 60 * 60 * 1000 - 1
        : Infinity;
      rows = rows.filter((row) => {
        const t = new Date(row.created_at).getTime();
        return t >= fromTime && t <= toTime;
      });
    }
    return rows;
  }, [conversions, searchQuery, statusFilter, dateFilter]);

  return {
    conversions,
    filteredConversions,
    totalLoaded: conversions.length,
    isFetching,
    isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    fetchNextPage,
    isLoading,
    isRefetching,
    refetch,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    dateFilter,
    setDateFilter
  };
};

export { useFetchWalletConversions };
